/**
 * Unified input: merges the touch twin-stick (mobile) with keyboard+mouse
 * (desktop). Touch takes precedence whenever a stick is being held; otherwise
 * keyboard/mouse drives. Touch zones are only mounted on coarse-pointer devices
 * so they never sit invisibly over a desktop mouse.
 */
import { createTouchInput } from "./touch.js";
import { createKeyboardMouseInput } from "./keyboardMouse.js";
import type { Scene } from "@babylonjs/core/scene.js";
import type { InputSample } from "./types.js";

export type { InputSample, InputSource } from "./types.js";

export interface Input {
  read(): InputSample;
  dispose(): void;
}

export const createInput = (
  scene: Scene,
  container: HTMLElement,
  getCharWorldPos: () => { x: number; z: number },
): Input => {
  const isTouch =
    window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window;
  const touch = isTouch ? createTouchInput(container) : null;
  const kbm = createKeyboardMouseInput(scene, getCharWorldPos);

  return {
    read: (): InputSample => (touch && touch.active ? touch.read() : kbm.read()),
    dispose(): void {
      touch?.dispose();
      kbm.dispose();
    },
  };
};
